import { COMMA } from '@angular/cdk/keycodes';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { Feature } from 'app/library/feature/models/feature.model';
import {
    CreateELearningModule,
    CreateELearningModuleFailure,
    CreateELearningModuleSuccess,
    CreateModule,
    CreateModuleFailure,
    CreateModuleSuccess,
    UpdateModuleSuccess,
} from 'app/library/module/actions';
import { UpdateModuleContent } from 'app/library/module/actions/update-module-content.action';
import { Module } from 'app/library/module/models/module.model';
import { CreateTag, CreateTagFailure, CreateTagSuccess, SearchTags } from 'app/library/tag/actions';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagState } from 'app/library/tag/models/tag.state';
import { TagsState } from 'app/library/tag/models/tags.state';
import { TagService } from 'app/library/tag/services/tag.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators';
import { FeatureState } from '../../../../feature/models/feature.state';
import { ELearningModule } from '../../../models/eLearning-module.model';
import { ModuleStatuses } from '../../../models/module-statuses';

@Component({
    selector: 'app-module-create-form-dialog',
    templateUrl: './create-form.component.html',
    styleUrls: ['./create-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class ModuleCreateFormDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    static readonly digicompDurationProperties = ['y', 'm', 'w', 'd', 'hh', 'mm'];
    separatorKeysCodes: number[] = [COMMA];

    @Select(FeatureState.getEntities)
    feature$: Observable<Feature[]>;

    @Select(TagState.getUnusedColors)
    unusedColor$: Observable<string[]>;
    unusedColors: string[];

    @Select(TagsState.getFilteredEntities)
    private _filteredTag$: Observable<MaxBrainTag[]>;
    tagTermExists: boolean;
    filteredTag$: Observable<MaxBrainTag[]>;

    @ViewChild('tagInput')
    tagInput: ElementRef<HTMLInputElement>;
    @ViewChild('auto')
    matAutocomplete: MatAutocomplete;
    tagCtrl = new FormControl();

    @ViewChild(MatStepper) stepper: MatStepper;

    @Input()
    role: string;

    @Input()
    moduleId: string;

    @Input()
    entityId: string;

    moduleForm: FormGroup;

    minDate: Date;
    maxDate: Date;
    createAnotherChecked = false;
    moduleType: string;
    isELearningVisible: boolean;
    isPrivateVisible: boolean;
    isStepOneActive = true;
    isFirstStepValid = false;
    isCreatingTag = false;

    get moduleDetailsForm(): FormGroup {
        return this.moduleForm && this.moduleForm.controls.details ? (this.moduleForm.controls.details as FormGroup) : null;
    }

    get moduleContentForm(): FormGroup {
        return this.moduleForm && this.moduleForm.controls.content ? (this.moduleForm.controls.content as FormGroup) : null;
    }

    creating = false;
    ruleOpts: {
        dontTrackProgress: string;
        trackProgress: string;
        sequential: string;
    };

    constructor(
        public dialogRef: MatDialogRef<ModuleCreateFormDialogComponent>,
        public route: ActivatedRoute,
        private _formBuilder: FormBuilder,
        private _store: Store,
        private _action$: Actions,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _router: Router,
        private _tagService: TagService
    ) {
        super();
        this.ruleOpts = ModuleStatuses.ruleOpts;

        const tags = this._tagService.getTags();
        if (!tags) {
            this._tagService.fetchTags();
        }
    }

    ngOnInit(): void {
        this.filteredTag$ = this._filteredTag$.pipe(
            filter((tags) => !!tags),
            tap((tags) => {
                this.tagTermExists = tags.map((tag) => tag.name.toLowerCase()).indexOf(this.tagCtrl?.value?.toLowerCase()) !== -1;
            })
        );

        this.route.children[1].children[0].params.pipe(filter(({ type }) => type !== 'choose')).subscribe(({ type }) => {
            this.moduleType = type;
            this.createFormGroup(type);

            if (this.moduleType === 'e-learning') {
                this.moduleDetailsForm.get('selfPaced').setValue(true);
            }
        });

        this.tagCtrl.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchTerm) => {
            this._store.dispatch(new SearchTags(searchTerm));
        });

        this.dialogRef.afterClosed().subscribe((result) => {
            if (result && result.createAnotherChecked) {
                const nextModuleType = this.moduleType === 'basic' ? (result.newModule.isPrivate ? 'private' : 'basic') : this.moduleType;

                this._router.navigate([{ outlets: { modal: null } }]).then(() => {
                    this._router.navigate([{ outlets: { modal: ['new-module', nextModuleType] } }]);
                });
            } else {
                this._router.navigate([{ outlets: { modal: null } }]);
            }
        });

        this.feature$
            .pipe(
                filter((features) => !!features),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((features) => {
                this.isPrivateVisible = features.filter((feature) => feature.id === 'module_visibility' && feature.status === 'active').length === 1;
                this.isELearningVisible = features.filter((feature) => feature.id === 'e_learning' && feature.status === 'active').length === 1;
            });

        this.unusedColor$.pipe(takeUntil(this._unsubscribeAll)).subscribe((unusedColors) => {
            this.unusedColors = unusedColors;
        });
    }

    onStepChange(event: StepperSelectionEvent): void {
        // waits for animation to finish transition between steps
        setTimeout(() => {
            if (event.selectedIndex === 0) {
                this.isStepOneActive = true;
            } else {
                this.isStepOneActive = false;
            }
        }, 400);
    }

    private _resetTagInputValue(): void {
        if (this.tagInput?.nativeElement) {
            this.tagInput.nativeElement.value = '';
        }
        this.tagCtrl.setValue(null);
    }

    private _addTag(tag: MaxBrainTag): void {
        if (!this.isCreatingTag) {
            this.isCreatingTag = true;

            this._store.dispatch(new CreateTag(tag));

            const actionSuccessSubscription = this._action$.pipe(ofActionDispatched(CreateTagSuccess), takeUntil(this._unsubscribeAll)).subscribe((action: CreateTagSuccess) => {
                this.moduleDetailsForm.controls.tags.setValue([...this.moduleDetailsForm.value.tags, action.payload]);
                this.moduleDetailsForm.markAsDirty();

                this._resetTagInputValue();
                actionSuccessSubscription.unsubscribe();
                actionFailureSubscription.unsubscribe();
                this.isCreatingTag = false;
            });

            const actionFailureSubscription = this._action$.pipe(ofActionDispatched(CreateTagFailure), takeUntil(this._unsubscribeAll)).subscribe(() => {
                this._resetTagInputValue();
                actionSuccessSubscription.unsubscribe();
                actionFailureSubscription.unsubscribe();
                this.isCreatingTag = false;
            });
        }
    }

    addTag(event: MatChipInputEvent): void {
        // Add tag only when MatAutocomplete is not open
        // To make sure this does not conflict with OptionSelected Event
        if (!this.matAutocomplete?.isOpen) {
            const value = event.value.trim();

            if (!value) {
                return;
            }

            const tagExists = this.moduleDetailsForm.value.tags.some((tag: MaxBrainTag) => tag.name === value);

            if (!tagExists) {
                this._addTag(
                    new MaxBrainTag({
                        id: 0,
                        name: value,
                        color: MaxBrainTag.getRandomColorFromList(this.unusedColors),
                        category: 'module',
                    })
                );
            }
        }
    }

    removeTag(tagToRemove: MaxBrainTag): void {
        this.moduleDetailsForm.controls.tags.setValue([...this.moduleDetailsForm.value.tags.filter((tag: MaxBrainTag) => tagToRemove.id !== tag.id)]);
        this.moduleDetailsForm.markAsDirty();
    }

    tagSelected(event: MatAutocompleteSelectedEvent): void {
        if (event.option.value instanceof MaxBrainTag) {
            this.moduleDetailsForm.controls.tags.setValue([...this.moduleDetailsForm.value.tags, event.option.value]);
            this.moduleDetailsForm.markAsDirty();

            this._resetTagInputValue();
        } else {
            this._addTag(
                new MaxBrainTag({
                    id: 0,
                    name: event.option.value,
                    color: MaxBrainTag.getRandomColorFromList(this.unusedColors),
                    category: 'module',
                })
            );
        }
    }

    tagUpdated(updatedTag: MaxBrainTag): void {
        this.moduleDetailsForm.patchValue({
            tags: this.moduleDetailsForm.controls.tags.value.map((tag) => {
                if (tag.id === updatedTag.id) {
                    return updatedTag;
                }
                return tag;
            }),
        });
    }

    createFormGroup(type: string): void {
        this.moduleForm = this._formBuilder.group({
            details: this._formBuilder.group({
                name: ['', Validators.required],
                startDate: ['', Validators.required],
                endDate: ['', Validators.required],
                selfPaced: [false, Validators.required],
                contentType: ['sections'],
                tags: [{ value: [], disabled: false }],
                // Kept in the form model (even though Duration step UI was removed),
                // so potential backend validation errors can still be mapped to form controls.
                duration: [''],
                digicompDuration: this._formBuilder.group(
                    ModuleCreateFormDialogComponent.digicompDurationProperties.reduce((accumulator, currentValue) => {
                        accumulator[currentValue] = ['', Validators.pattern(/^\d*(?:[.,]\d{1})?$/)];
                        return accumulator;
                    }, {})
                ),
            }),
            content: this._formBuilder.group({
                id: ['', Validators.required],
                structure: [true, Validators.required],
                rule: [{ value: this.ruleOpts.trackProgress, disabled: false }],
            }),
        });

        this._updateFirstStepValid();
        this.moduleDetailsForm.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._updateFirstStepValid();
        });

        this.moduleDetailsForm
            .get('selfPaced')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value: boolean) => {
                if (value) {
                    this.moduleDetailsForm.get('startDate').clearValidators();
                    this.moduleDetailsForm.get('endDate').clearValidators();
                    this.moduleDetailsForm.get('startDate').setValue(null);
                    this.moduleDetailsForm.get('endDate').setValue(null);
                } else {
                    this.moduleDetailsForm.get('startDate').setValidators(Validators.required);
                    this.moduleDetailsForm.get('endDate').setValidators(Validators.required);
                }

                this.moduleDetailsForm.get('startDate').updateValueAndValidity();
                this.moduleDetailsForm.get('endDate').updateValueAndValidity();
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _updateFirstStepValid(): void {
        const details = this.moduleDetailsForm;

        if (!details) {
            this.isFirstStepValid = false;
            return;
        }

        const hasName = !!details.get('name')?.value;
        const selfPaced = !!details.get('selfPaced')?.value;
        const hasRequiredDates = selfPaced || (!!details.get('startDate')?.value && !!details.get('endDate')?.value);

        this.isFirstStepValid = hasName && hasRequiredDates;
    }

    private _openSnackBar(message: string): MatSnackBarRef<SimpleSnackBar> {
        return this._matSnackBar.open(message, this._translateService.instant('GENERAL.BUTTON.EDIT'), {
            verticalPosition: 'top',
            duration: SnackBarTime.slow,
        });
    }

    private _getFormData(type: string): any {
        const formData = this.moduleForm.getRawValue();

        if (type !== 'e-learning') {
            formData.details = Object.assign({}, formData.details, {
                hasSections: formData.content.structure,
                sectionRule: formData.content.rule ? formData.content.rule : 'default',
                isPrivate: type === 'private',
            });
        }

        return formData;
    }

    /**
     *
     * @param newModule
     * @private
     */
    private _closeDialogOnSuccess(newModule: Module): void {
        this.dialogRef.close({ newModule, createAnotherChecked: this.createAnotherChecked });
        this.creating = true;

        const successMessage = this._translateService.instant('MODULE.MESSAGE.CREATED');

        this._openSnackBar(successMessage)
            .onAction()
            .subscribe(() => {
                this.goToModule(newModule);
            });

        return;
    }

    private _createSuccessCallback(failureSubscription: Subscription, newModule: Module, contentFormData: { structure: boolean } = null): void {
        failureSubscription.unsubscribe();

        const actions: any[] = [];

        if (contentFormData && this.moduleType !== 'e-learning') {
            actions.push(new UpdateModuleContent(Object.assign({}, newModule, { hasSections: contentFormData.structure })));
        }

        if (actions.length) {
            this._store.dispatch(actions);
        }

        if (newModule instanceof ELearningModule || newModule.selfPaced) {
            // if we create a E-learning module we can close the dialog
            this._closeDialogOnSuccess(newModule);
        }

        // UpdateModule need to be play before to redirect to edit module : section update
        this._action$.pipe(ofActionDispatched(UpdateModuleSuccess), take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._closeDialogOnSuccess(newModule);
        });
    }

    private _createFailureCallback(successSubscription: Subscription, response: HttpErrorResponse): void {
        successSubscription.unsubscribe();

        this.dialogRef.updateSize('80%');
        this.creating = false;

        /**
         * setTimeout is needed because of this.creating is used for ngIf in template
         */
        setTimeout(() => {
            Object.keys(response.error.errors).forEach((errorfield) => {
                (this.moduleDetailsForm.controls[errorfield] as FormControl).setErrors(response.error.errors[errorfield]);
            });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Go to module
     */
    goToModule(module: Module): void {
        this._store.dispatch(new Navigate([`/${Module.label_plural}/${module.id}/edit`]));
    }

    updateEndDate(): void {
        const startDate = this.moduleDetailsForm.get('startDate').value;

        this.minDate = startDate ? new Date(startDate) : null;
    }

    updateStartDate(): void {
        const endDate = this.moduleDetailsForm.get('endDate').value;

        this.maxDate = endDate ? new Date(endDate) : null;
    }

    goBack(stepper: MatStepper): void {
        stepper.previous();
    }

    goForward(stepper: MatStepper): void {
        if (!this.isFirstStepValid) {
            this.moduleDetailsForm?.markAllAsTouched();
            return;
        }

        stepper.next();
    }

    create(type: string): void {
        this.dialogRef.updateSize('300px');
        this.creating = true;

        const tempFormData = this.moduleForm.getRawValue();

        let formData: any;
        if (type === 'e-learning') {
            formData = tempFormData.details.contentType === 'sections' ? this._getFormData('basic') : this._getFormData(type);
        } else {
            formData = this._getFormData(type);
        }

        let CreateAction: any = CreateModule;
        let CreateActionSuccess: any = CreateModuleSuccess;
        let CreateActionFailure: any = CreateModuleFailure;

        if (type === 'e-learning' && tempFormData.details.contentType === 'e_learning_package') {
            CreateAction = CreateELearningModule;
            CreateActionSuccess = CreateELearningModuleSuccess;
            CreateActionFailure = CreateELearningModuleFailure;
        }

        this._store.dispatch(new CreateAction(formData.details));
        const successSubscription = this._action$.pipe(ofActionDispatched(CreateActionSuccess), take(1), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            this._createSuccessCallback(failureSubscription, action.payload, formData.content);
        });
        const failureSubscription = this._action$.pipe(ofActionDispatched(CreateActionFailure), take(1), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            this._createFailureCallback(successSubscription, action.payload.response);
        });
    }

    handleChange($event: MatRadioChange): void {
        if (!$event.value) {
            this.moduleContentForm.controls.rule.setValue(false);
            this.moduleContentForm.controls.rule.disable();
        } else {
            this.moduleContentForm.controls.rule.enable();
            this.moduleContentForm.controls.rule.setValue(this.ruleOpts.trackProgress);
        }
    }
}
